import type { OnboardingSubscriptionPlan } from '@houzy/schemas/partner-cockpit/onboarding';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { planFeatures, planPresentation } from '../planPresentation';
import { PlanFeatureList, PopularPlanBadge } from './PlanCardParts';

export type SubscriptionPlanCardProps = {
    plan: OnboardingSubscriptionPlan;
    index: number;
    loading?: boolean;
    actionLabel?: string;
    onChoose: () => void;
};

export function SubscriptionPlanCard({
    plan,
    index,
    loading = false,
    actionLabel,
    onChoose,
}: SubscriptionPlanCardProps): ReactElement {
    const { t } = useTranslation();
    const { tierKey, highlighted } = planPresentation(index);

    return (
        <Paper
            variant="outlined"
            sx={{
                position: 'relative',
                flex: 1,
                minWidth: 0,
                display: 'flex',
                borderColor: 'primary.main',
                borderWidth: 2,
                px: 2,
                py: 3,
            }}>
            {highlighted && <PopularPlanBadge />}

            <Stack spacing={3} sx={{ width: '100%' }}>
                <Stack spacing={2.5} sx={{ alignItems: 'center' }}>
                    <Stack spacing={0} sx={{ alignItems: 'center' }}>
                        {tierKey && (
                            <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
                                {t(tierKey)}
                            </Typography>
                        )}
                        <Typography variant="h6">
                            {t('ONBOARDING.SUBSCRIPTION.PLAN_NAME', { months: plan.commitmentLengthInMonths })}
                        </Typography>
                    </Stack>

                    <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
                        <Typography variant="h3">{plan.pricePerMonth}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('ONBOARDING.SUBSCRIPTION.PER_MONTH')}
                        </Typography>
                    </Stack>
                </Stack>

                <Stack spacing={3}>
                    <Button
                        variant="contained"
                        fullWidth
                        loading={loading}
                        endIcon={<ChevronRightIcon />}
                        onClick={onChoose}>
                        {actionLabel ?? t('ONBOARDING.SUBSCRIPTION.CONTINUE_TO_CHECKOUT')}
                    </Button>

                    <PlanFeatureList features={planFeatures(plan)} />
                </Stack>
            </Stack>
        </Paper>
    );
}
